import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
} from "@coreui/react";
import {
  debounce,
  Paper,
  StyledEngineProvider,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmBox from "../../../components/ConfirmBox/ConfirmBox";
import { SearchElement } from "../../../components/Search/Search";
import { TableBodyElement } from "../../../components/TableBody/TableBodyElement";
import { TableHeadElement } from "../../../components/TableBody/TableHeadElement";
import {
  useActiveInactiveBlogCategoryMutation,
  useDeleteBlogCategoriesMutation,
  useGetBlogCategoriesListQuery,
} from "../../../redux-services/api/BlogApi";
import { ColumnsType, Order, PageMeta } from "../../../types";

const tableColumns: ColumnsType[] = [
  {
    id: "id",
    sort: false,
    label: "#",
  },
  {
    id: "name",
    sort: true,
    label: "Category Name",
  },
  {
    id: "action",
    sort: false,
    label: "Action",
    style: { width: "15%" },
  },
];

export interface SortingData {
  id: string;
  name: string;
  status: boolean;
  createdAt: string;
  updatedAt?: string;
}

let initialOrder: string = process.env.REACT_APP_ORDER as string;
const initialState: PageMeta = {
  page: 1,
  per_page: Number(process.env.REACT_APP_PER_PAGE),
  searchText: "",
  order: process.env.REACT_APP_ORDER,
  order_by: process.env.REACT_APP_ORDER_BY,
};

export const BlogCategoriesList: React.FunctionComponent = (props) => {
  let navigate = useNavigate();
  let query = useLocation();
  const [page, setPage] = useState<number>(1);
  const [orderBy, setOrderBy] = useState<keyof SortingData>(
    process.env.REACT_APP_ORDER_BY as keyof SortingData
  );
  const [order, setOrder] = useState<Order>(initialOrder as Order);
  const [search, setSearch] = useState<string>("");
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    initialState.per_page as number
  );
  const [blogListState, setBlogListState] = useState<PageMeta>(initialState);
  const [isClose, setIsClose] = useState<boolean>(false);
  const [Id, setId] = useState<string>("");
  const [activeInactiveBlog, editStatus] =
    useActiveInactiveBlogCategoryMutation();

  const { isSuccess, isLoading, data } = useGetBlogCategoriesListQuery({
    per_page: blogListState.per_page ?? rowsPerPage,
    page: query.state ?? blogListState.page,
    order: blogListState.order ?? order,
    order_by: blogListState.order_by ?? orderBy,
    searchText: blogListState.searchText ?? search,
  });
  const [deleteBlog, response] = useDeleteBlogCategoriesMutation();

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
    setBlogListState({ ...blogListState, page: newPage + 1 });

    // Condition for manage state of table list.
    if (query.state !== null) {
      navigate(query.pathname, {});
    }
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setBlogListState({
      ...blogListState,
      per_page: parseInt(event.target.value),
      page: 1,
    });
    setPage(1);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof SortingData
  ) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);
    setBlogListState({
      ...blogListState,
      order: newOrder,
      order_by: property,
    });
  };

  const createSortHandler = (
    event: React.MouseEvent<unknown>,
    property: keyof SortingData
  ) => {
    if (property !== ("action" as keyof SortingData)) {
      handleRequestSort(event, property);
    }
  };

  const searchHandler = useMemo(() => {
    return debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
      setBlogListState({
        page: 1,
        per_page: blogListState.per_page,
        order: initialOrder,
        searchText: event.target.value,
      });
    }, 800);
  }, []);
  const confirmBoxClose = (res: boolean) => {
    setIsClose(res);
    if (res) {
      setIsClose(false);
      deleteBlog(Id);
    } else {
      setIsClose(res);
    }
  };

  const changeStatus = (id: string, status: boolean) => {
    activeInactiveBlog({ id, status });
  };

  return (
    <CRow>
      <CCol xs={12}>
        <CCard className="mb-4">
          <CCardHeader
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <strong>Blog Categories List</strong>
            <Tooltip title="Add Blog Categories" arrow>
              <CButton
                size="sm"
                color="primary"
                variant="outline"
                onClick={() => navigate("/blog/categories/add")}
              >
                Add Blog Categories
              </CButton>
            </Tooltip>
          </CCardHeader>
          <CCardBody className="bg-transparent">
            <SearchElement
              searchFn={searchHandler}
              searchTag={"Search Blog Categories..."}
            />
            <StyledEngineProvider>
              <TableContainer component={Paper}>
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="simple table bordered"
                  className="table-bordered table"
                >
                  <TableHead>
                    <TableRow>
                      <TableHeadElement
                        setHead={tableColumns ? tableColumns : []}
                        order={order}
                        orderBy={orderBy}
                        sortFn={(event, id: string) =>
                          createSortHandler(event, id as keyof SortingData)
                        }
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableBodyElement
                      selectedColumns={["name"]}
                      setData={data?.response.data ? data?.response.data : []}
                      editFn={(id) =>
                        navigate(`/blog/categories/edit/${id}`, {
                          state: page,
                        })
                      }
                      statusFn={(id: string, status: boolean) => {
                        changeStatus(id, status);
                      }}
                      deleteFn={(id) => {
                        setIsClose(true);
                        setId(id);
                      }}
                      pageData={{
                        limit:
                          rowsPerPage ?? Number(process.env.REACT_APP_PER_PAGE),
                        page: data?.response.page as number,
                      }}
                    />
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={data?.response.total ?? 0}
                  page={data?.response.page ?? 0}
                  rowsPerPageOptions={[20, 50, 100]}
                  onPageChange={handleChangePage}
                  rowsPerPage={
                    rowsPerPage ?? Number(process.env.REACT_APP_PER_PAGE)
                  }
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </StyledEngineProvider>
          </CCardBody>
        </CCard>
      </CCol>
      <ConfirmBox
        title="Blog Categories Confirmation"
        message="Are you sure, you want to delete this blog categories?"
        open={isClose}
        onClose={confirmBoxClose}
      />
    </CRow>
  );
};
